import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink, CalendarClock, XCircle, Trash2, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api, { formatApiError } from '../api';
import { isoToParisParts, parisPartsToIso, durationMinutes, formatParisRange } from '../tz';
import { INTERVENTION_STATUS } from '../constants';

const TYPE_LABELS = { intervention: 'Intervention', pause: 'Pause', indisponibilite: 'Indisponibilité' };

const EventDetailDialog = ({ open, onClose, event, onChanged }) => {
  const navigate = useNavigate();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('60');
  const [status, setStatus] = useState('');
  const [savingResched, setSavingResched] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && event) {
      const p = isoToParisParts(event.start);
      setDate(p.date); setTime(p.time);
      setDuration(String(durationMinutes(event.start, event.end) || 60));
      setStatus(event.status || '');
    }
  }, [open, event]);

  if (!event) return null;
  const isIntervention = event.type === 'intervention';
  const cancelled = event.status === 'annulee';

  const reschedule = async () => {
    setSavingResched(true);
    try {
      const startIso = parisPartsToIso(date, time);
      const endIso = new Date(new Date(startIso).getTime() + parseInt(duration, 10) * 60000).toISOString();
      const { data } = await api.patch(`/events/${event.id}`, { start: startIso, end: endIso });
      if (data.warnings?.length) toast.warning(`Reprogrammé — chevauchement (${data.warnings.length})`);
      else toast.success('Reprogrammé');
      onChanged?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingResched(false); }
  };

  const changeStatus = async () => {
    setSavingStatus(true);
    try {
      await api.patch(`/events/${event.id}`, { status });
      toast.success('Statut mis à jour');
      onChanged?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingStatus(false); }
  };

  const remove = async () => {
    const msg = isIntervention ? 'Annuler ce rendez-vous ? Le dossier et son historique seront conservés.' : 'Supprimer cet élément ?';
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      await api.delete(`/events/${event.id}`);
      toast.success(isIntervention ? 'Rendez-vous annulé' : 'Supprimé');
      onChanged?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="event-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">{TYPE_LABELS[event.type]}</span>
            {event.interventionNumber || event.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1 text-sm">
          <p className="font-medium text-neutral-900">{event.title}</p>
          {event.clientName && <p className="text-neutral-600">Client : {event.clientName}</p>}
          <p className="text-neutral-500">{formatParisRange(event.start, event.end)}</p>
          {cancelled && <p className="text-red-600 font-medium">Rendez-vous annulé</p>}
        </div>

        {isIntervention && (
          <Button variant="outline" className="w-full mt-2" data-testid="open-fiche-button"
            onClick={() => { onClose(); navigate(`/admin/interventions/${event.interventionId}`); }}>
            <ExternalLink className="w-4 h-4 mr-2" />Ouvrir la fiche complète
          </Button>
        )}

        {!cancelled && (
          <div className="border-t border-neutral-100 pt-3 mt-3 space-y-3">
            <p className="text-xs font-semibold text-neutral-500 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />Reprogrammer</p>
            <div className="grid grid-cols-3 gap-2">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="resched-date" />
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} step="900" data-testid="resched-time" />
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger data-testid="resched-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 min</SelectItem>
                  <SelectItem value="60">1 h</SelectItem>
                  <SelectItem value="90">1 h 30</SelectItem>
                  <SelectItem value="120">2 h</SelectItem>
                  <SelectItem value="180">3 h</SelectItem>
                  <SelectItem value="240">4 h</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={reschedule} disabled={savingResched} variant="outline" className="w-full" data-testid="reschedule-button">
              {savingResched ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer le nouveau créneau
            </Button>

            {isIntervention && (
              <div className="pt-2">
                <Label className="text-xs">Statut</Label>
                <div className="flex gap-2">
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="event-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(INTERVENTION_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={changeStatus} disabled={savingStatus} className="bg-red-700 hover:bg-red-600" data-testid="save-status-button">
                    {savingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : 'OK'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <Button onClick={remove} disabled={deleting} variant="ghost"
          className="w-full text-red-600 hover:text-red-700 mt-2" data-testid="cancel-event-button">
          {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (isIntervention ? <XCircle className="w-4 h-4 mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />)}
          {isIntervention ? 'Annuler le rendez-vous' : 'Supprimer'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default EventDetailDialog;
